/**
 * App Message API Service
 *
 * API methods for app message operations (messages sent by tenant applications to users).
 * Communicates with the Runtime API.
 *
 * Endpoints:
 * - GET /tenants/:tenantId/apps/:applicationId/users/:userId/messages
 * - GET /tenants/:tenantId/apps/:applicationId/users/:userId/messages/unread-count
 * - GET /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId/read
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/read-all
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId/archive
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId/unarchive
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId/delete
 * - POST /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId/restore
 * - DELETE /tenants/:tenantId/apps/:applicationId/users/:userId/messages/:messageId
 */

import runtimeApiClient from '../../../api/runtime-api-client';
import type {
  AppMessageSanitized,
  AppMessageListQuery,
  AppMessageListResponse,
  AppMessageUnreadCountResponse,
  AppMessageMarkAllAsReadResponse,
} from '../../../types/app-message.types';

// ============================================================================
// APP MESSAGE SERVICE
// ============================================================================

export const appMessageService = {
  /**
   * List app messages for a user
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param query - Query parameters (unreadOnly, type, limit, cursor)
   * @returns List of messages with optional cursor for pagination
   */
  listMessages: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    query?: AppMessageListQuery
  ): Promise<AppMessageListResponse> => {
    const response = await runtimeApiClient.get<AppMessageListResponse>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages`,
      { params: query }
    );
    return response.data;
  },

  /**
   * Get unread message count for badge display
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @returns Unread message count
   */
  getUnreadCount: async (
    tenantId: string,
    applicationId: string,
    userId: string
  ): Promise<number> => {
    const response = await runtimeApiClient.get<AppMessageUnreadCountResponse>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/unread-count`
    );
    return response.data.count;
  },

  /**
   * Get a single app message by ID
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns App message
   */
  getMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.get<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}`
    );
    return response.data;
  },

  /**
   * Mark a single message as read
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns Updated message
   */
  markAsRead: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/read`
    );
    return response.data;
  },

  /**
   * Mark all messages as read for a user
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @returns Number of messages marked as read
   */
  markAllAsRead: async (
    tenantId: string,
    applicationId: string,
    userId: string
  ): Promise<number> => {
    const response = await runtimeApiClient.post<AppMessageMarkAllAsReadResponse>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/read-all`
    );
    return response.data.count;
  },

  /**
   * Archive a message (removes from inbox view)
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns Archived message
   */
  archiveMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/archive`
    );
    return response.data;
  },

  /**
   * Unarchive a message (moves back to inbox)
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns Unarchived message
   */
  unarchiveMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/unarchive`
    );
    return response.data;
  },

  /**
   * Soft delete a message (moves to deleted view)
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns Deleted message
   */
  softDeleteMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/delete`
    );
    return response.data;
  },

  /**
   * Restore a deleted message (moves back to inbox)
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   * @returns Restored message
   */
  restoreMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<AppMessageSanitized>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/restore`
    );
    return response.data;
  },

  /**
   * Permanently delete a message
   *
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  permanentDeleteMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<void> => {
    await runtimeApiClient.delete(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}`
    );
  },

  // ============================================================================
  // Cross-Application Methods (no applicationId required)
  // ============================================================================

  /**
   * List all app messages for a user across all applications
   *
   * @param tenantId - Tenant UUID
   * @param userId - User UUID
   * @param query - Query parameters (view, unreadOnly, type, limit, cursor)
   * @returns List of messages from all applications with optional cursor for pagination
   */
  listAllMessages: async (
    tenantId: string,
    userId: string,
    query?: AppMessageListQuery
  ): Promise<AppMessageListResponse> => {
    const response = await runtimeApiClient.get<{
      success: boolean;
      data: AppMessageSanitized[];
      pagination: { hasMore: boolean; cursor?: string | null };
    }>(`/tenants/${tenantId}/users/${userId}/messages`, { params: query });
    return {
      messages: response.data.data,
      cursor: response.data.pagination?.cursor ?? undefined,
    };
  },

  /**
   * Get total unread message count across all applications
   *
   * @param tenantId - Tenant UUID
   * @param userId - User UUID
   * @returns Total unread message count across all applications
   */
  getTotalUnreadCount: async (
    tenantId: string,
    userId: string
  ): Promise<number> => {
    const response = await runtimeApiClient.get<{
      success: boolean;
      data: { count: number };
    }>(`/tenants/${tenantId}/users/${userId}/messages/unread-count`);
    return response.data.data.count;
  },
};

export default appMessageService;
